import { BodySecondary, LabelStrong } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import styled from 'styled-components';

import { useAppSelector } from '@/redux/hooks';
import { getCatalogDetailCurators } from '@/redux/modules/catalog/detail/catalogDetail.selectors';
import { getCuratorBaseUrl } from '@/redux/modules/config';
import { getCuratorImageUrl } from '@/utils/urls';

import DescriptionSection from '../ItemDescription/DescriptionSection';

type Curator = {
    expertise: string;
    id: number;
    imageFileName: string;
    name: string;
};

type Props = {
    catalogId: number;
    className?: string;
};

const imageSize = 140;

const CuratorInfo = ({ catalogId, className }: Props) => {
    const catalogCurators = useAppSelector((state) => getCatalogDetailCurators(state, catalogId));
    const curatorBaseUrl = useAppSelector(getCuratorBaseUrl);

    const mapCurators = React.useCallback(
        (curator: Curator) => {
            return (
                <CuratorDiv key={curator.id}>
                    <ImageContainerDiv>
                        <StyledImage
                            alt={curator.name}
                            height={70}
                            loading="lazy"
                            src={getCuratorImageUrl(curatorBaseUrl, curator.id, curator.imageFileName, imageSize)}
                            width={70}
                        />
                    </ImageContainerDiv>
                    <DetailsText>
                        <LabelStrong>{curator.name}</LabelStrong>
                        <BodySecondary color="secondary">{curator.expertise}</BodySecondary>
                    </DetailsText>
                    <div style={{ clear: 'both' }} />
                </CuratorDiv>
            );
        },
        [curatorBaseUrl]
    );

    if (!catalogCurators.length) {
        return null;
    }

    return (
        <CuratorInfoDiv
            body={catalogCurators.map(mapCurators)}
            className={className}
            header="bidding_console.console.currentLiveItem.curator.curatedBy"
        />
    );
};

export default React.memo<Props>(CuratorInfo);

const CuratorDiv = styled.div`
    display: flex;
    margin-top: 16px;
`;

const ImageContainerDiv = styled.div`
    align-items: center;
    border-radius: 50%;
    display: flex;
    height: 70px;
    justify-content: center;
    min-height: 70px;
    min-width: 70px;
    overflow: hidden;
    position: relative;
    width: 70px;
`;

const StyledImage = styled.img`
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
`;

const DetailsText = styled.div`
    margin-left: 12px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: center;
`;

const CuratorInfoDiv = styled(DescriptionSection)`
    padding: 20px 0;
`;

CuratorInfoDiv.displayName = 'CuratorInfoDiv';
